import { useCallback, useEffect, useState } from 'react'

import { OfMassMessagesService } from '@api'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'
import { saveAs } from 'file-saver'

import InfoIcon from '@/assets/icons/icon-info.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import { Tooltip } from '@/components/ui/tooltip'
import { Tab } from '@/dialogs/MassMessagesStatisticsDialog/Tab.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $creatorId } from '@/models/creators'
import { toast } from '@/models/toasts'
import { TagList } from '@/modules/chatting/ui/components/tags-list'
import { PriorityStatsTable } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/PriorityStatsTable.tsx'
import { RegularStatsTable } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/RegularStatsTable.tsx'
import {
  MassMessageTablesTypes,
  priorityMassMessageFilterTypes,
} from '@/modules/chatting/ui/sidebar/mass-messages-statistics/constants.ts'
import {
  $filterKey,
  setFilterKey,
} from '@/modules/chatting/ui/sidebar/mass-messages-statistics/models/massMessages.ts'
import {
  changeQuery,
  resetNormalMassMessages,
} from '@/modules/chatting/ui/sidebar/mass-messages-statistics/models/normalMassMessagesStats.ts'
import {
  $priorityType,
  changePriorityType,
  resetPriorityMassMessagesStats,
} from '@/modules/chatting/ui/sidebar/mass-messages-statistics/models/priorityMassMessagesStats.ts'
import { handleError } from '@/utils/error'

interface Props extends DialogProps {}

export const MassMessagesStatisticsDialog = ({ isOpen, onClose }: Props) => {
  const [isExporting, setIsExporting] = useState(false)
  const creatorId = useUnit($creatorId)

  const filterKey = useUnit($filterKey)
  const priorityMassMessagesType = useUnit($priorityType)

  useEffect(() => {
    setFilterKey(MassMessageTablesTypes.NMM)
  }, [])

  const handleResetStats = useCallback(() => {
    resetNormalMassMessages()
    resetPriorityMassMessagesStats()
  }, [])

  const isPMM = filterKey === MassMessageTablesTypes.PMM

  const handleExportCSV = async () => {
    if (!isPMM) {
      try {
        setIsExporting(true)

        const response = await OfMassMessagesService.exportMassMessagesStats({
          creatorId: creatorId ?? 0,
        })
        const blob = new Blob([response], { type: 'text/csv' })

        saveAs(
          blob,
          `mass-messages-stats-${format(new Date(), 'yyyy-MM-dd')}.csv`,
        )

        toast.success('CSV exported successfully')
      } catch (error) {
        handleError(error, 'Failed to export CSV')
      } finally {
        setIsExporting(false)
      }
    }
  }

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      afterLeave={handleResetStats}
      showCloseIcon={false}
      panelClassName="lg:max-w-[1380px]"
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Mass Message Statistics</Dialog.Title>
        </Dialog.Header>
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <div className="border-secondary-400 dark:bg-secondary-600 flex w-96 items-center gap-1 rounded-md border bg-white p-1">
              {[MassMessageTablesTypes.NMM, MassMessageTablesTypes.PMM].map(
                (tab) => (
                  <Tab
                    key={tab}
                    tab={tab}
                  />
                ),
              )}
            </div>
            <Tooltip
              content="Mass Message sending is managed directly by OnlyFans. If messages appear as unsent or missing, this behavior originates from the OnlyFans platform and is not caused by CreatorHero."
              contentClassName="text-left max-w-[400px]"
            >
              <InfoIcon className="text-primary-500 h-5 w-5 cursor-pointer" />
            </Tooltip>
            {isPMM && (
              <>
                <div className="bg-secondary-400 h-9 w-px" />
                <TagList
                  selectedItems={[priorityMassMessagesType.id]}
                  list={priorityMassMessageFilterTypes}
                  onClickItem={changePriorityType}
                  withHorizontalScroll={false}
                />
              </>
            )}
          </div>

          {!isPMM && (
            <>
              <Button
                variant="default"
                onClick={handleExportCSV}
                className="ml-auto"
                isLoading={isExporting}
              >
                Download CSV
              </Button>
              <TextField
                placeholder="Search"
                className="max-w-[300px] resize-none"
                prefix={
                  <SearchIcon className="text-title dark:text-secondary-300" />
                }
                onChange={changeQuery}
              />
            </>
          )}
        </div>
        <div className="relative max-h-[548px] overflow-hidden rounded-2xl border px-6">
          {isPMM ? <PriorityStatsTable /> : <RegularStatsTable />}
        </div>
        <Dialog.ClosableFooter
          onClose={onClose}
          className="grid-cols-1"
          closeText="Close"
        />
      </div>
    </Dialog>
  )
}
